export interface SearchResult {
	path: string;
	matchType: 'key' | 'value';
	preview: string;
}

export interface DiffEntry {
	type: 'added' | 'removed' | 'modified';
	path: string;
	oldValue?: unknown;
	newValue?: unknown;
}

export function deepClone<T>(value: T): T {
	return structuredClone(value);
}

// --- Path helpers ---

export function getAtPath(obj: unknown, path: string): unknown {
	if (path === '') return obj;
	const segments = parsePath(path);
	let current = obj;
	for (const seg of segments) {
		if (current === null || current === undefined) return undefined;
		if (Array.isArray(current)) {
			current = current[Number(seg)];
		} else if (typeof current === 'object') {
			current = (current as Record<string, unknown>)[seg];
		} else {
			return undefined;
		}
	}
	return current;
}

export function setAtPath(obj: unknown, path: string, value: unknown): unknown {
	if (path === '') return value;
	const root = deepClone(obj);
	const segments = parsePath(path);
	let current: unknown = root;
	for (let i = 0; i < segments.length - 1; i++) {
		const seg = segments[i];
		if (Array.isArray(current)) {
			current = current[Number(seg)];
		} else if (typeof current === 'object' && current !== null) {
			current = (current as Record<string, unknown>)[seg];
		}
	}
	const lastSeg = segments[segments.length - 1];
	if (Array.isArray(current)) {
		current[Number(lastSeg)] = value;
	} else if (typeof current === 'object' && current !== null) {
		(current as Record<string, unknown>)[lastSeg] = value;
	}
	return root;
}

export function deleteAtPath(obj: unknown, path: string): unknown {
	if (path === '') return undefined;
	const root = deepClone(obj);
	const segments = parsePath(path);
	let current: unknown = root;
	for (let i = 0; i < segments.length - 1; i++) {
		const seg = segments[i];
		if (Array.isArray(current)) {
			current = current[Number(seg)];
		} else if (typeof current === 'object' && current !== null) {
			current = (current as Record<string, unknown>)[seg];
		}
	}
	const lastSeg = segments[segments.length - 1];
	if (Array.isArray(current)) {
		current.splice(Number(lastSeg), 1);
	} else if (typeof current === 'object' && current !== null) {
		delete (current as Record<string, unknown>)[lastSeg];
	}
	return root;
}

export function buildPath(parent: string, key: string | number): string {
	if (parent === '') return String(key);
	return `${parent}.${key}`;
}

function parsePath(path: string): string[] {
	return path.split('.');
}

export function parentPath(path: string): string {
	const segments = parsePath(path);
	return segments.slice(0, -1).join('.');
}

export function ancestorPaths(path: string): string[] {
	const segments = parsePath(path);
	const result: string[] = [];
	for (let i = 1; i <= segments.length; i++) {
		result.push(segments.slice(0, i).join('.'));
	}
	return result;
}

// --- Smart type inference ---

export function inferType(raw: string): unknown {
	if (raw.startsWith('"') && raw.endsWith('"') && raw.length >= 2) {
		return raw.slice(1, -1);
	}
	if (raw === 'null') return null;
	if (raw === 'true') return true;
	if (raw === 'false') return false;
	if (raw === '') return '';
	const num = Number(raw);
	if (Number.isFinite(num) && raw.trim() !== '') return num;
	if (
		(raw.startsWith('{') && raw.endsWith('}')) ||
		(raw.startsWith('[') && raw.endsWith(']'))
	) {
		try {
			return JSON.parse(raw);
		} catch {
			// not valid JSON, treat as string
		}
	}
	return raw;
}

export function isNumericToggleable(value: unknown): boolean {
	if (typeof value === 'number') return true;
	if (typeof value === 'string') {
		const n = Number(value);
		return Number.isFinite(n) && value.trim() !== '';
	}
	return false;
}

export function toggleNumericType(value: unknown): unknown {
	if (typeof value === 'number') return String(value);
	if (typeof value === 'string') {
		const n = Number(value);
		if (Number.isFinite(n)) return n;
	}
	return value;
}

export function displayValue(value: unknown): string {
	if (value === null) return 'null';
	if (value === undefined) return 'undefined';
	if (typeof value === 'string') return value;
	return String(value);
}

// --- Search ---

export function searchJson(
	data: unknown,
	query: string,
	path = '',
): SearchResult[] {
	if (!query) return [];
	const lowerQuery = query.toLowerCase();
	const results: SearchResult[] = [];

	function walk(value: unknown, currentPath: string) {
		if (value === null || value === undefined) {
			if ('null'.includes(lowerQuery) || 'undefined'.includes(lowerQuery)) {
				results.push({
					path: currentPath,
					matchType: 'value',
					preview: String(value),
				});
			}
			return;
		}

		if (Array.isArray(value)) {
			value.forEach((item, index) => {
				const childPath = buildPath(currentPath, index);
				walk(item, childPath);
			});
			return;
		}

		if (typeof value === 'object') {
			for (const [key, val] of Object.entries(
				value as Record<string, unknown>,
			)) {
				const childPath = buildPath(currentPath, key);
				if (key.toLowerCase().includes(lowerQuery)) {
					results.push({ path: childPath, matchType: 'key', preview: key });
				}
				walk(val, childPath);
			}
			return;
		}

		const strVal = String(value).toLowerCase();
		if (strVal.includes(lowerQuery)) {
			results.push({
				path: currentPath,
				matchType: 'value',
				preview: String(value),
			});
		}
	}

	walk(data, path);
	return results;
}

// --- Deep diff ---

export function deepDiff(
	original: unknown,
	current: unknown,
	path = '',
): DiffEntry[] {
	const diffs: DiffEntry[] = [];

	if (original === current) return diffs;

	if (
		original === null ||
		original === undefined ||
		current === null ||
		current === undefined ||
		typeof original !== typeof current ||
		typeof original !== 'object'
	) {
		if (original !== current) {
			if (original !== undefined && current === undefined) {
				diffs.push({ type: 'removed', path, oldValue: original });
			} else if (original === undefined && current !== undefined) {
				diffs.push({ type: 'added', path, newValue: current });
			} else {
				diffs.push({
					type: 'modified',
					path,
					oldValue: original,
					newValue: current,
				});
			}
		}
		return diffs;
	}

	const origIsArray = Array.isArray(original);
	const currIsArray = Array.isArray(current);
	if (origIsArray !== currIsArray) {
		diffs.push({
			type: 'modified',
			path,
			oldValue: original,
			newValue: current,
		});
		return diffs;
	}

	if (origIsArray && currIsArray) {
		const maxLen = Math.max(original.length, current.length);
		for (let i = 0; i < maxLen; i++) {
			const childPath = path ? `${path}.${i}` : String(i);
			if (i >= original.length) {
				diffs.push({ type: 'added', path: childPath, newValue: current[i] });
			} else if (i >= current.length) {
				diffs.push({ type: 'removed', path: childPath, oldValue: original[i] });
			} else {
				diffs.push(...deepDiff(original[i], current[i], childPath));
			}
		}
		return diffs;
	}

	const origObj = original as Record<string, unknown>;
	const currObj = current as Record<string, unknown>;
	const allKeys = new Set([...Object.keys(origObj), ...Object.keys(currObj)]);
	for (const key of allKeys) {
		const childPath = path ? `${path}.${key}` : key;
		if (!(key in origObj)) {
			diffs.push({ type: 'added', path: childPath, newValue: currObj[key] });
		} else if (!(key in currObj)) {
			diffs.push({ type: 'removed', path: childPath, oldValue: origObj[key] });
		} else {
			diffs.push(...deepDiff(origObj[key], currObj[key], childPath));
		}
	}

	return diffs;
}

// --- Add helpers ---

export function addObjectKey(obj: unknown, path: string, key: string): unknown {
	const root = deepClone(obj);
	const target = path === '' ? root : getAtPath(root, path);
	if (typeof target === 'object' && target !== null && !Array.isArray(target)) {
		(target as Record<string, unknown>)[key] = null;
	}
	return root;
}

export function addArrayElement(obj: unknown, path: string): unknown {
	const root = deepClone(obj);
	const target = path === '' ? root : getAtPath(root, path);
	if (Array.isArray(target)) {
		target.push(null);
	}
	return root;
}

// --- Key rename ---

export function renameKeyAtPath(
	obj: unknown,
	path: string,
	newKey: string,
): { data: unknown; newPath: string } {
	const segments = parsePath(path);
	const oldKey = segments[segments.length - 1];
	const parentSegments = segments.slice(0, -1);
	const parentPathStr = parentSegments.join('.');

	if (oldKey === newKey) {
		return { data: obj, newPath: path };
	}

	const root = deepClone(obj);
	const parent = parentPathStr === '' ? root : getAtPath(root, parentPathStr);

	if (typeof parent === 'object' && parent !== null && !Array.isArray(parent)) {
		const parentObj = parent as Record<string, unknown>;
		const rebuilt: Record<string, unknown> = {};
		for (const key of Object.keys(parentObj)) {
			if (key === oldKey) {
				rebuilt[newKey] = parentObj[oldKey];
			} else {
				rebuilt[key] = parentObj[key];
			}
		}
		for (const key of Object.keys(parentObj)) delete parentObj[key];
		for (const [key, val] of Object.entries(rebuilt)) parentObj[key] = val;
	}

	const newPath = parentPathStr ? `${parentPathStr}.${newKey}` : newKey;
	return { data: root, newPath };
}

// --- Tab-order computation ---

export interface EditTarget {
	path: string;
	target: 'key' | 'value';
}

export function getVisibleEditTargets(
	data: unknown,
	expandedPaths: Set<string>,
	sortAlpha: boolean,
): EditTarget[] {
	const results: EditTarget[] = [];

	function walk(value: unknown, currentPath: string, keyEditable: boolean) {
		if (keyEditable) {
			results.push({ path: currentPath, target: 'key' });
		}
		if (value === null || value === undefined || typeof value !== 'object') {
			results.push({ path: currentPath, target: 'value' });
			return;
		}
		if (!expandedPaths.has(currentPath)) return;

		if (Array.isArray(value)) {
			for (let i = 0; i < value.length; i++) {
				walk(value[i], buildPath(currentPath, i), false);
			}
		} else {
			const entries = Object.entries(value as Record<string, unknown>);
			const sorted = sortAlpha
				? [...entries].sort(([a], [b]) => a.localeCompare(b))
				: entries;
			for (const [key, val] of sorted) {
				walk(val, buildPath(currentPath, key), true);
			}
		}
	}

	walk(data, '', false);
	return results;
}

export function truncate(str: string, maxLen = 60): string {
	if (str.length <= maxLen) return str;
	return str.slice(0, maxLen - 1) + '\u2026';
}

export function formatValue(value: unknown): string {
	if (value === null) return 'null';
	if (value === undefined) return 'undefined';
	if (typeof value === 'string') return `"${truncate(value)}"`;
	if (typeof value === 'object') {
		const json = JSON.stringify(value);
		return truncate(json);
	}
	return String(value);
}
